/**
 * Recharge l'application quand un chunk lazy (JS/CSS) reste introuvable — onglet resté ouvert
 * pendant un déploiement (hash de bundle absents du CDN), ou réseau client qui n'arrive pas à
 * télécharger le chunk.
 *
 * C'est la seule porte de sortie : `React.lazy` mémoïse la promesse rejetée pour toute la vie de
 * la page, donc sans rechargement l'utilisateur reste bloqué sur l'écran d'erreur même en
 * réessayant.
 *
 * Garde anti-boucle : pas de rechargement si un précédent a eu lieu il y a moins de
 * RELOAD_DEBOUNCE_MS — le chunk manque toujours, une nouvelle tentative bouclerait. L'appelant
 * laisse alors l'erreur remonter à l'ErrorBoundary. Un chargement nominal réarme naturellement la
 * garde (l'horodatage vieillit), donc un futur déploiement déclenchera bien un nouveau reload.
 */
const RELOAD_GUARD_KEY = 'rm:chunk-reload-at';
const RELOAD_DEBOUNCE_MS = 10_000;

/** Renvoie `true` si un rechargement a été déclenché, `false` si la garde l'a refusé. */
export function reloadForStaleChunkOnce(): boolean {
    // L'accès au sessionStorage peut throw (Safari navigation privée, iframe sandboxée). On
    // l'isole pour ne JAMAIS empêcher le rechargement : c'est notre seule porte de sortie.
    let lastReloadAt = 0;
    try {
        lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
    } catch {
        lastReloadAt = 0;
    }

    if (Date.now() - lastReloadAt < RELOAD_DEBOUNCE_MS) {
        return false;
    }

    try {
        sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    } catch {
        // Storage indisponible : pas de garde anti-boucle, mais on recharge quand même.
    }

    window.location.reload();

    return true;
}
